import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';
import { isEmpty, isObject, isArray, cloneDeep } from 'lodash';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import { getCostingList, setCostingSelectedIds, filterSortData } from '../../../Dashboard/TodoList/CostingTsx/store/Costing.action';
import { Page_Size, imageUrl, checkCurrency, shortenandHoverString, roundOff } from '../../../../Components/helper';
import Loader from '../../../../Components/Utility/Loader/Loader';
import { fetchRPProductInfoDetails } from '../store/ProductList.action';
import { useComponent } from '../../../UserSetting/UsersLibrary/UserRights';
import noImage from '../../../../assets/images/no-image.png';
import CommonImageComponent from '../../../../Components/Utility/CommonImage';
import CustomTooltip from '../../../../Components/Tooltip/CustomTooltip';
import { getSampleRefLabel, resolveCostingInfoLabel } from '../../../Dashboard/TodoList/CostingTsx/costing-todo.helpers';

export function ProductCostingTable({ activeCostingTemplate, getSelectedCount, getSelectedIdsWithDevIds, isCostingDataLoading }) {
  const {
    CostingTodoListData: {
      averageTotalCal,
      costingListData,
      costingListTotalCount,
      costingSelectedIds,
      costingFilterValues: {
        searchText,
        pageIndex,
        pageSize,
        sortOrder,
        sortPropertyName,
        loading: reduxLoading,
        supplier,
        colourway,
        currency,
        incoterms,
        selectedcosting_Status,
        selectedBrand,
        selectedSubBrand,
        sampleRef,
        selectedSeason,
        selectedYear,
        selectedMonth,
        selectedProductType,
        selectedCat1,
        selectedCat2,
        selectedCat3,
        selectedCat4,
        selectedCompanyUser,
        selectedDesigner,
        selectedCoo,
        productGroupId,
      },
    },
    Users: { logInRights },
    UserDetails: {
      userRole,
      isSupplierLoggedIn,
      userInfo: { userId },
    },
    CostingTemplateData: { costingTemplateList },
    SystemMastersDetails: { subBrandAccess },
    ProductListData: { rangePlanProductInfo },
  } = useSelector((reduxState) => reduxState);

  const getSelectedColumns = () => {
    return [
      'reference',
      'sampleRef',
      'supplier',
      'factory',
      'moqNeeded',
      'colourway',
      'size',
      'length',
      'incoterm',
      'shipmentMethod',
      'currency',
      'targetCost',
      'quantity',
      'pricePerUnitIncoterm',
      'totalIncoterm',
    ];
  };

  const selectedColumns = getSelectedColumns();
  const { CheckActionPermission } = useComponent();
  const [expandedRowIndex, setExpandedRowIndex] = useState([]);
  const [isExpandAll, setIsExpandAll] = useState(false);
  const [innerExpandedRowIndex, setInnerExpandedRowIndex] = useState({});
  const MasterAddEditAccess = CheckActionPermission(logInRights, 'products', 'costing', 'addEdit');
  const MasterDeleteAccess = CheckActionPermission(logInRights, 'products', 'costing', 'delete');
  const dispatch = useDispatch();

  const [initState, setInitState] = useState({
    hasMore: true,
    loading: false,
    totalRecords: 0,
    costingData: [],
  });
  const { t } = useTranslation('common');

  useEffect(() => {
    setInitState((inInfinitestate) => ({
      ...inInfinitestate,
      hasMore: true,
      loading: false,
      totalRecords: 0,
      costingData: [],
    }));
    setExpandedRowIndex([]);
    setIsExpandAll(false);
    setInnerExpandedRowIndex({});
  }, [activeCostingTemplate]);

  useEffect(() => {
    setInitState((inInfinitestate) => ({
      ...inInfinitestate,
      loading: reduxLoading,
    }));
  }, [reduxLoading]);

  useEffect(() => {
    if (Array.isArray(costingListData)) {
      setInitState((inInfinitestate) => ({
        ...inInfinitestate,
        costingData: costingListData,
      }));
    }
  }, [costingListData]);

  useEffect(() => {
    if (costingListTotalCount > costingListData?.length) {
      setInitState((inInfinitestate) => ({
        ...inInfinitestate,
        hasMore: true,
      }));
    } else
      setInitState((inInfinitestate) => ({
        ...inInfinitestate,
        hasMore: false,
      }));
  }, [costingListData?.length, costingListTotalCount]);

  const getCostingListData = async (data, number) => {
    setInitState((toGetstate) => ({
      ...toGetstate,
      loading: true,
      ...data,
    }));

    if (rangePlanProductInfo?.params?.apiName === 'product-costing') {
      const baseParams = rangePlanProductInfo?.params || {};
      const nextPageParams = {
        ...baseParams,
        pageIndex: number || data.pageIndex,
      };
      await dispatch(fetchRPProductInfoDetails(nextPageParams, 'COSTING_SCROLL'));
    } else {
      const params = {
        pageIndex: number || data.pageIndex,
        pageSize: data.pageSize,
        searchText,
        isScrolled: true,
        costingTemplateId: activeCostingTemplate,
        type: 'costing',
        sortOrder,
        sortPropertyName,
        supplier,
        colourway,
        currency,
        incoterms,
        status: selectedcosting_Status,
        brand: selectedBrand,
        sampleRef,
        season: selectedSeason,
        year: selectedYear,
        month: selectedMonth,
        productType: selectedProductType,
        catOne: selectedCat1,
        catTwo: selectedCat2,
        catThree: selectedCat3,
        catFour: selectedCat4,
        createdBy: selectedCompanyUser,
        designer: selectedDesigner,
        COO: selectedCoo,
        productGroupId,
        ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
      };
      if (isSupplierLoggedIn) params.supplierId = userId;
      await dispatch(getCostingList(params));
    }
    setInitState((inInfinitestate) => ({
      ...inInfinitestate,
      loading: false,
    }));
  };

  const handlePageNextInScroll = async () => {
    if (rangePlanProductInfo?.params?.apiName === 'product-costing') {
      const currentIndex = rangePlanProductInfo?.params?.pageIndex || 1;
      const nextIndex = currentIndex + 1;
      const obj = {
        pageIndex: nextIndex,
        pageSize: rangePlanProductInfo?.params?.pageSize || pageSize,
      };
      await getCostingListData(obj, nextIndex);
      return;
    }
    let index = pageIndex;
    index += 1;
    const params = {
      pageIndex: index,
      pageSize,
      searchText,
      totalRecords: costingListTotalCount,
      type: 'costing',
      sortOrder,
      sortPropertyName,
      supplier,
      colourway,
      currency,
      incoterms,
      status: selectedcosting_Status,
      brand: selectedBrand,
      sampleRef,
      season: selectedSeason,
      year: selectedYear,
      month: selectedMonth,
      productType: selectedProductType,
      catOne: selectedCat1,
      catTwo: selectedCat2,
      catThree: selectedCat3,
      catFour: selectedCat4,
      createdBy: selectedCompanyUser,
      designer: selectedDesigner,
      COO: selectedCoo,
      productGroupId,
      ...(subBrandAccess ? { subBrand: selectedSubBrand } : {}),
      costingTemplateId: activeCostingTemplate,
    };
    const obj = {
      pageIndex: index,
      pageSize,
    };
    await dispatch(filterSortData(obj));
    getCostingListData(params);
  };

  const selectAllProduct = (value) => {
    let previousSelected = costingSelectedIds;
    if (typeof value === 'boolean') {
      if (value) previousSelected = initState?.costingData?.map((data) => data?._id);
      else previousSelected = [];
    } else if (value) {
      if (previousSelected?.includes(value)) {
        previousSelected = previousSelected?.filter((data) => data !== value);
      } else {
        previousSelected = [...previousSelected, value];
      }
    }
    dispatch(setCostingSelectedIds(previousSelected));
    if (getSelectedCount) {
      getSelectedCount(previousSelected);
    }
    if (
      getSelectedIdsWithDevIds &&
      Array.isArray(initState?.costingData) &&
      initState.costingData.length > 0 &&
      Array.isArray(previousSelected) &&
      previousSelected.length > 0
    ) {
      const isRpCosting = rangePlanProductInfo?.params?.apiName === 'product-costing';
      const selectedRows =
        initState.costingData?.filter((vl) => {
          const id = vl?._id;
          return id != null && previousSelected.some((pid) => String(pid) === String(id));
        }) ?? [];
      const filteredCostingIds = isRpCosting
        ? selectedRows.flatMap((vls) => {
            const devOrderId = vls?._id ?? vls?.devOrderId ?? vls?.initialData?.devOrderId;
            const devOrderIdStr = typeof devOrderId === 'string' ? devOrderId : devOrderId?.toString?.() ?? '';
            const costings = [vls?.initialData, ...(Array.isArray(vls?.expandData) ? vls.expandData : [])].filter(Boolean);
            return costings
              .map((c) => ({
                costingId: c?._id != null ? (typeof c._id === 'string' ? c._id : c._id?.toString?.()) : '',
                devOrderId: devOrderIdStr,
              }))
              .filter((x) => x.costingId);
          })
        : selectedRows.map((vls) => ({
            costingId: vls?._id,
            devOrderId: vls?.devOrderId,
          }));
      getSelectedIdsWithDevIds(filteredCostingIds);
    } else if (getSelectedIdsWithDevIds) {
      getSelectedIdsWithDevIds([]);
    }
  };

  const isSupplier = userRole === 'Supplier';
  const isRangePlanCosting = rangePlanProductInfo?.params?.apiName === 'product-costing';
  const activeCostingTemplateDetailes = useMemo(() => {
    return costingTemplateList?.find((item) => item?._id === activeCostingTemplate);
  }, [costingTemplateList?.length, activeCostingTemplate]);

  useEffect(() => {
    if (isExpandAll && initState?.costingData?.length > 0) {
      setExpandedRowIndex(initState.costingData.map((_, index) => index));
    }
  }, [initState?.costingData?.length]);

  const expandMoreData = (index) => {
    if (expandedRowIndex.includes(index)) {
      setIsExpandAll(false);
      setExpandedRowIndex(expandedRowIndex.filter((item) => item !== index));
    } else {
      setExpandedRowIndex([...expandedRowIndex, index]);
    }
  };

  const toggleInnerExpand = (parentIndex, childIndex) => {
    const key = `${parentIndex}-${childIndex}`;
    setInnerExpandedRowIndex((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const topColumnsArr = useMemo(() => {
    if (activeCostingTemplateDetailes?.topColumnArr?.length) {
      return activeCostingTemplateDetailes?.topColumnArr?.filter((item) => item?.isActive);
    }
  }, [activeCostingTemplateDetailes?.topColumnArr]);

  const defaultColumnsArr = useMemo(() => {
    if (activeCostingTemplateDetailes?.defaultColumns?.length) {
      return activeCostingTemplateDetailes?.defaultColumns?.filter((item) => item?.isActive);
    }
  }, [activeCostingTemplateDetailes?.defaultColumns]);

  const bottomColumnsArr = useMemo(() => {
    if (activeCostingTemplateDetailes?.bottomColumnArr?.length) {
      return activeCostingTemplateDetailes?.bottomColumnArr?.filter((item) => item?.isActive);
    }
  }, [activeCostingTemplateDetailes?.bottomColumnArr]);

  const isCostingInfoActive = activeCostingTemplateDetailes?.isCostingInfoActive ?? false;

  const defaultCostingInfo = useMemo(() => (activeCostingTemplateDetailes?.costingInfo ?? []).filter((ci) => ci?.isDefault), [activeCostingTemplateDetailes]);

  const dynamicCostingInfo = useMemo(
    () => (activeCostingTemplateDetailes?.costingInfo ?? []).filter((ci) => !ci?.isDefault).sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0)),
    [activeCostingTemplateDetailes],
  );

  /** `slot` is a canonical 0-based index into the default field order, not the stored `position` — see `resolveCostingInfoLabel`. */
  const getCostingInfoLabel = (slot, fallback) => resolveCostingInfoLabel(defaultCostingInfo, slot, fallback, isCostingInfoActive);

  const getDynamicCostingValue = (cost, item) => {
    const snap = cost?.productTemplate?.costingInfo?.find((ci) => String(ci?._id) === String(item?._id));
    const value = snap?.value;
    if (value === undefined || value === null || value === '') return '--';
    if (item?.type === 'datePicker') {
      const parsedDate = moment(value);
      return parsedDate.isValid() ? parsedDate.format('DD-MM-YY') : '--';
    }
    return String(value);
  };

  const expandAll = () => {
    if (isExpandAll) {
      setIsExpandAll(false);
      setExpandedRowIndex([]);
    } else {
      setIsExpandAll(true);
      setExpandedRowIndex(initState?.costingData?.map((_, index) => index) || []);
    }
  };

  const setCurrency = (currency) => {
    return checkCurrency(currency, undefined, true) || '';
  };

  const isZeroAmount = (value) => Number(value ?? 0) === 0;

  const formatCurrencyValue = (currency, value, hasExpanded = false) => {
    if (hasExpanded) return 'Multiple';
    if (isZeroAmount(value)) return '0';
    return `${currency}${value}`.trim();
  };

  const formatPercentageValue = (value) => (isZeroAmount(value) ? '0' : `${value} %`);

  const getValue = (value) => {
    if (isObject(value)) {
      return value?.name || '';
    }
    return value || '--';
  };
  useEffect(() => {
    if (rangePlanProductInfo?.params?.apiName === 'product-costing') {
      const doc = Array.isArray(rangePlanProductInfo?.data?.doc) ? rangePlanProductInfo.data.doc : [];
      const pageIdx = rangePlanProductInfo?.params?.pageIndex || 1;
      setInitState((s) => ({
        ...s,
        costingData: pageIdx > 1 ? [...(s.costingData || []), ...doc] : doc,
        totalRecords: rangePlanProductInfo?.data?.totalCount || 0,
        hasMore: doc?.length > 0 && pageIdx * (rangePlanProductInfo?.params?.pageSize || Page_Size) < (rangePlanProductInfo?.data?.totalCount || 0),
      }));
    }
  }, [rangePlanProductInfo]);

  const getLandedValue = (data, _id, keyType) => {
    if (keyType === 'percentage') {
      return data?.find((item) => item?._id === _id && item?.key === keyType)?.calculatedValue;
    }
    return data?.find((item) => item?._id === _id && item?.key === keyType)?.tempValue;
  };

  const imgSrc = (element) => {
    if (!element?.image || isEmpty(element?.image) || element?.image === null) {
      return noImage;
    }
    if (element.image.includes('https://') || element.image.includes('http://')) {
      return element.image;
    }
    return `${imageUrl}/${element.image}`;
  };

  /**
   * Opens the product costing detail for a costing entry in a new tab.
   * The dev order id is resolved from the parent row when the costing entry does not carry one:
   * entries taken from expandedData hold only the costing _id, so reading devOrderId off the entry
   * alone produced a /development-order/undefined/... URL. Mirrors the resolution already used when
   * building the bulk-selection payload.
   * @param {object} item Costing entry — initialData or an expandedData entry.
   * @param {object} row Parent product row the costing belongs to.
   * @returns {void}
   */
  const handleEdit = (item, row) => {
    const devOrderId = item?.devOrderId ?? (isRangePlanCosting ? row?._id : row?.devOrderId) ?? row?.initialData?.devOrderId;
    if (!devOrderId || !item?._id) {
      return;
    }
    window.open(`${window.location.origin}/development-order/${devOrderId}/product-costing-detail/${item._id}`, '_blank');
  };

  const renderContent = () => {
    if (isCostingDataLoading) {
      return <Loader />;
    }

    if (initState?.costingData?.length > 0) {
      return (
        <InfiniteScroll
          dataLength={initState?.costingData?.length}
          hasMore={initState.hasMore}
          next={handlePageNextInScroll}
          loader={reduxLoading && <Loader />}
          className="fixed-col-container rp-table-container"
          scrollableTarget="rpcostingtablecontainer"
        >
          <table id="rpcostingtablecontainer" className={`table common-custom-table fixed-header mb-0 ${isSupplier ? 'supplier-table' : ''}`}>
            <thead>
              <tr>
                {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && (
                  <th className="w-56px">
                    <div className="common-checkbox margin-auto">
                      <input
                        id="all_costing"
                        type="checkbox"
                        onChange={(e) => selectAllProduct(e.target.checked)}
                        checked={costingSelectedIds?.length === initState?.costingData?.length && initState?.costingData?.length > 0}
                      />
                      <label htmlFor="all_costing" className="chklabel" />
                    </div>
                  </th>
                )}
                <th className="w-56px">
                  <button type="button" className="btn-up-down" aria-label={t('COMMON_LABEL.COLLAPSE')} onClick={() => expandAll()}>
                    {isExpandAll ? <span className="icon-new-down-arrow-icon icon-inverse" /> : <span className="icon-new-down-arrow-icon " />}
                  </button>
                </th>
                <th className="w-80px">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PRODUCT_IMAGE')}</th>
                <th className="w-150px">{t('PRODUCT_CRITICAL_PATHS.DETAIL_PRODUCT_CP.PRODUCT_NUMBER')}</th>
                {selectedColumns?.includes('reference') && (
                  <th className="w-150px first-fix-col">
                    <span className="cursor-pointer">{getCostingInfoLabel(0, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.COSTING_REFERENCE'))}</span>
                  </th>
                )}
                {selectedColumns?.includes('supplier') && (
                  <th className="w-224px"> {getCostingInfoLabel(1, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.SUPLIER'))}</th>
                )}
                {selectedColumns?.includes('factory') && (
                  <th className="w-224px">{getCostingInfoLabel(2, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.FACTORY'))}</th>
                )}
                {selectedColumns?.includes('currency') && (
                  <th>
                    <span className="cursor-pointer">{getCostingInfoLabel(4, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.CURRENCY'))}</span>
                  </th>
                )}
                {selectedColumns?.includes('moqNeeded') && (
                  <th className="w-150px">{getCostingInfoLabel(5, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.MOQ_NEEDED'))}</th>
                )}
                {selectedColumns?.includes('targetCost') && (
                  <th>
                    <span className="cursor-pointer">{getCostingInfoLabel(6, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.TARGET_COST'))}</span>
                  </th>
                )}
                {selectedColumns?.includes('incoterm') && (
                  <th className="w-200px">
                    <span className="cursor-pointer">{getCostingInfoLabel(7, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.INCOTERMS'))}</span>
                  </th>
                )}
                {selectedColumns?.includes('shipmentMethod') && (
                  <th>
                    <span className="cursor-pointer">{getCostingInfoLabel(8, t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.SHIPMENT_METHOD'))}</span>
                  </th>
                )}
                {selectedColumns?.includes('sampleRef') && (
                  <th className="w-150px">{getCostingInfoLabel(9, t('costing:DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.SAMPLE_REF'))}</th>
                )}
                {isCostingInfoActive &&
                  dynamicCostingInfo?.map((dynCol, indxDyn) => (
                    <th className="w-150px" key={`dyn-head-${dynCol?._id || indxDyn}`}>
                      {dynCol?.aliasDisplayName || dynCol?.title}
                    </th>
                  ))}
                <th>{t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.COSTING_TYPE')}</th>
                {selectedColumns?.includes('colourway') && <th className="w-224px">{t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.COLOUR_WAY')}</th>}
                {selectedColumns?.includes('size') && <th className="w-224px">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.SIZE_COLUMN')}</th>}
                {selectedColumns?.includes('length') && <th>{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.LENGTH_COLUMN')}</th>}
                {selectedColumns?.includes('quantity') && (
                  <th>
                    {averageTotalCal?.sums?.quantityPerSizCol &&
                    !Number.isNaN(averageTotalCal?.sums?.quantityPerSizCol) &&
                    Number(averageTotalCal?.sums?.quantityPerSizCol) ? (
                      <span className="floating-value">{roundOff(averageTotalCal?.sums?.quantityPerSizCol, 2)}</span>
                    ) : null}
                    {t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.QUANTITY')}
                  </th>
                )}
                {selectedColumns?.includes('pricePerUnitIncoterm') && (
                  <th className="w-150px">{t('DEVLOPMENT_ORDER.EXPAND_VIEW.PRODUCT_COSTING_TABLE.PRICE_PER_UNIT')} (Incoterms)</th>
                )}
                {selectedColumns?.includes('totalIncoterm') && (
                  <th>
                    {averageTotalCal?.sums?.totalInco && !Number.isNaN(averageTotalCal?.sums?.totalInco) && Number(averageTotalCal?.sums?.totalInco) ? (
                      <span className="floating-value">{roundOff(averageTotalCal?.sums?.totalInco, 2)}</span>
                    ) : null}
                    {t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.TOTAL_INCOTERMS')}
                  </th>
                )}

                {!isSupplierLoggedIn && (
                  <>
                    {isArray(topColumnsArr) &&
                      topColumnsArr?.map((topCol, indx) => {
                        return (
                          <th key={indx} className="w-150px">
                            {averageTotalCal?.sums[topCol?.aliasName] &&
                            !Number.isNaN(averageTotalCal?.sums[topCol?.aliasName]) &&
                            Number(averageTotalCal?.sums[topCol?.aliasName]) ? (
                              <span className="floating-value">{roundOff(averageTotalCal?.sums[topCol?.aliasName], 2)}</span>
                            ) : null}
                            {topCol?.aliasDisplayName || topCol?.title}
                          </th>
                        );
                      })}
                    {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                      activeCostingTemplateDetailes?.landedInfo
                        ?.filter((vl) => vl?.isActive)
                        ?.map((topCol, indx) => {
                          return (
                            <th key={indx} className="w-150px">
                              {topCol?.aliasDisplayName || topCol?.title} {`${topCol?.type.includes('percentage') ? '(%)' : ''}`}
                            </th>
                          );
                        })}
                    {Array.isArray(defaultColumnsArr) &&
                      !isEmpty(defaultColumnsArr) &&
                      defaultColumnsArr?.map((defaultCol) => {
                        return defaultCol?.isActive ? (
                          <th className="w-120px" key={defaultCol?._id}>
                            {averageTotalCal?.sums[defaultCol?.aliasName] &&
                            !Number.isNaN(averageTotalCal?.sums[defaultCol?.aliasName]) &&
                            Number(averageTotalCal?.sums[defaultCol?.aliasName]) ? (
                              <span className="floating-value">{roundOff(averageTotalCal?.sums[defaultCol?.aliasName], 2)}</span>
                            ) : null}
                            {defaultCol?.aliasDisplayName || defaultCol?.title}
                          </th>
                        ) : null;
                      })}
                    {isArray(bottomColumnsArr) &&
                      bottomColumnsArr?.map((bottomCol) => {
                        return bottomCol?.isActive ? (
                          <th className="w-150px">
                            {(averageTotalCal?.averages[bottomCol?.aliasName] || averageTotalCal?.sums[bottomCol?.aliasName]) &&
                            !Number.isNaN(averageTotalCal?.averages[bottomCol?.aliasName] || averageTotalCal?.sums[bottomCol?.aliasName]) &&
                            Number(averageTotalCal?.averages[bottomCol?.aliasName] || averageTotalCal?.sums[bottomCol?.aliasName]) ? (
                              <span
                                className="floating-value"
                                style={averageTotalCal?.sums[bottomCol?.aliasName] ? { backgroundColor: '#1E90FF' } : { backgroundColor: '#FF892F' }}
                              >
                                {averageTotalCal?.averages[bottomCol?.aliasName]
                                  ? `${roundOff(averageTotalCal?.averages[bottomCol?.aliasName], 2)}%`
                                  : roundOff(averageTotalCal?.sums[bottomCol?.aliasName], 2)}
                              </span>
                            ) : null}
                            {bottomCol?.aliasDisplayName || bottomCol?.title}
                          </th>
                        ) : null;
                      })}
                  </>
                )}
                <th className="w-150px last-fix-col">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.STATUS')}</th>
              </tr>
            </thead>
            {initState?.costingData?.map((cost, i) => {
              const productImage = imgSrc(cost);

              let expandedList = [];
              let hasExpanded = false;
              let rowOneData = {};
              let firstCostingHasExpanded = false;
              let defaultCostingEntry = null;

              let defaultExpandedList = [];

              if (isRangePlanCosting) {
                const costingEntries = [];
                if (cost?.initialData) {
                  costingEntries.push(cost.initialData);
                }
                if (Array.isArray(cost?.expandData) && cost.expandData.length > 0) {
                  costingEntries.push(...cost.expandData);
                }
                const uniqueCostingEntries = costingEntries.reduce(
                  (acc, entry) => {
                    const key = entry?._id || entry?.id || entry?.reference || entry?.productInfo?.reference;
                    if (!key) {
                      acc.list.push(entry);
                      return acc;
                    }
                    if (!acc.seen.has(key)) {
                      acc.seen.add(key);
                      acc.list.push(entry);
                    }
                    return acc;
                  },
                  { seen: new Set(), list: [] },
                ).list;
                expandedList = uniqueCostingEntries.sort((a, b) => {
                  if (a.isDefault && !b.isDefault) return -1;
                  if (!a.isDefault && b.isDefault) return 1;
                  const aIndex = uniqueCostingEntries.indexOf(a);
                  const bIndex = uniqueCostingEntries.indexOf(b);
                  return aIndex - bIndex;
                });
                defaultCostingEntry = expandedList[0];
                const rawDefaultExpandedList =
                  defaultCostingEntry && Array.isArray(defaultCostingEntry?.expandedData) && defaultCostingEntry.expandedData.length > 0
                    ? defaultCostingEntry.expandedData
                    : [];
                defaultExpandedList = rawDefaultExpandedList.reduce(
                  (acc, combo) => {
                    const colourwayId = combo?.colourway?._id || combo?.colourway?.id || combo?.colourway || '';
                    const sizeId = combo?.size || '';
                    const lengthId = combo?.length?._id || combo?.length?.id || combo?.length?.name || combo?.length || '';
                    const key = `${colourwayId}__${sizeId}__${lengthId}`;
                    if (!acc._seen.has(key)) {
                      acc._seen.add(key);
                      acc.list.push(combo);
                    }
                    return acc;
                  },
                  { _seen: new Set(), list: [] },
                ).list;

                firstCostingHasExpanded = Array.isArray(defaultExpandedList) && defaultExpandedList.length > 1;
                hasExpanded = expandedList.length > 1;
                rowOneData = (Array.isArray(defaultExpandedList) && defaultExpandedList[0]) || {};
              } else {
                const unsortedList =
                  Array.isArray(cost?.expandData) && cost.expandData.length > 0
                    ? cost.expandData
                    : Array.isArray(cost?.initialData?.expandedData) && cost.initialData.expandedData.length > 0
                    ? cost.initialData.expandedData
                    : Array.isArray(cost?.expandedData)
                    ? cost.expandedData
                    : [];
                expandedList = unsortedList.sort((a, b) => {
                  if (a.isDefault && !b.isDefault) return -1;
                  if (!a.isDefault && b.isDefault) return 1;
                  const aIndex = unsortedList.indexOf(a);
                  const bIndex = unsortedList.indexOf(b);
                  return aIndex - bIndex;
                });
                hasExpanded = expandedList.length > 0;
                rowOneData = expandedList?.[0] || {};
              }

              const parentCostingEntry = isRangePlanCosting ? defaultCostingEntry || cost?.initialData : cost?.initialData;
              const childCostingList = isRangePlanCosting && expandedList.length > 1 ? expandedList.slice(1) : expandedList;
              const defaultHasCombos = isRangePlanCosting ? Array.isArray(defaultExpandedList) && defaultExpandedList.length > 1 : false;

              return (
                <tbody key={cost?._id}>
                  <tr>
                    {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && (
                      <td className="w-50px">
                        <div className="common-checkbox margin-auto">
                          <input
                            id={`costing_checkbox_${cost?._id}_${i}`}
                            type="checkbox"
                            onChange={(e) => selectAllProduct(cost?._id)}
                            checked={costingSelectedIds?.includes(cost?._id)}
                          />
                          <label htmlFor={`costing_checkbox_${cost?._id}_${i}`} className="chklabel" />
                        </div>
                      </td>
                    )}
                    <td>
                      {hasExpanded && (
                        <button type="button" className="btn-up-down" aria-label={t('COMMON_LABEL.COLLAPSE')} onClick={() => expandMoreData(i)}>
                          {expandedRowIndex?.includes(i) ? (
                            <span className="icon-new-down-arrow-icon icon-inverse" />
                          ) : (
                            <span className="icon-new-down-arrow-icon" />
                          )}
                        </button>
                      )}
                    </td>

                    <td>
                      <CommonImageComponent productImage={productImage} />
                    </td>
                    <td>
                      {' '}
                      <div className="d-flex align-items-center">
                        {shortenandHoverString(cost?.number || '--', 20)}
                        <button type="button" className="btn ml-auto" aria-label="info">
                          <OverlayTrigger
                            placement={t('TAKING_DATES.BUTTON')}
                            trigger="hover"
                            rootClose
                            overlay={
                              <Popover className="custom-popover">
                                <Popover.Content>
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.BRAND')}: </b>
                                    {getValue(cost?.brandId)}
                                  </p>
                                  {subBrandAccess && (
                                    <p className="mb-8">
                                      <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.SUB_BRAND')}: </b>
                                      {getValue(cost?.subBrandId)}
                                    </p>
                                  )}
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.PRODUCT_TYPES')}: </b>
                                    {getValue(cost?.supplierType)}
                                  </p>
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.DESCRIPTION')}: </b>
                                    {getValue(cost?.description)}
                                  </p>
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.SEASON')}: </b>
                                    {getValue(cost?.seasonId)}
                                  </p>
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.MONTH')}: </b>
                                    {getValue(cost?.month)}
                                  </p>
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.YEAR')}: </b>
                                    {getValue(cost?.year)}
                                  </p>
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.CATEGORY_1')}: </b>
                                    {getValue(cost?.catOneId)}
                                  </p>{' '}
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.CATEGORY_2')}: </b>
                                    {getValue(cost?.catTwoId)}
                                  </p>{' '}
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.CATEGORY_3')}: </b>
                                    {getValue(cost?.catThreeId)}
                                  </p>{' '}
                                  <p className="mb-8">
                                    <b className="text-bold">{t('PLACEHOLDER_MANAGEMENTS.PRODUCT_EDIT_MODAL.CATEGORY_4')}: </b>
                                    {getValue(cost?.catFourId)}
                                  </p>
                                </Popover.Content>
                              </Popover>
                            }
                          >
                            <span className="icon icon-Information f-20" />
                          </OverlayTrigger>
                        </button>
                      </div>
                    </td>
                    {selectedColumns?.includes('reference') && (
                      <td className="first-fix-col">
                        {isRangePlanCosting && expandedList.length === 1 && firstCostingHasExpanded ? (
                          <>
                            <button
                              type="button"
                              className="btn p-0 d-inline-flex align-items-center"
                              aria-label={t('COMMON_LABEL.COLLAPSE')}
                              onClick={() => toggleInnerExpand(i, 0)}
                              style={{ padding: '0' }}
                            >
                              <span style={{ fontSize: '14px', padding: '0' }}>
                                {shortenandHoverString(cost?.initialData?.productInfo?.reference || cost?.initialData?.reference || '--', 24)}
                              </span>
                              {innerExpandedRowIndex?.[`${i}-0`] ? (
                                <span className="icon-new-down-arrow-icon icon-inverse ml-2" />
                              ) : (
                                <span className="icon-new-down-arrow-icon ml-2" />
                              )}
                            </button>
                            <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                              <button type="button" className="btn d-inline-flex p-0" aria-label="button" onClick={() => handleEdit(parentCostingEntry, cost)}>
                                <span className="icon-redirect f-15" />
                              </button>
                            </CustomTooltip>
                          </>
                        ) : isRangePlanCosting && expandedList.length > 1 ? (
                          defaultHasCombos ? (
                            <>
                              <button
                                type="button"
                                className="btn p-0 d-inline-flex align-items-center"
                                aria-label={t('COMMON_LABEL.COLLAPSE')}
                                onClick={() => {
                                  if (!expandedRowIndex?.includes(i)) {
                                    expandMoreData(i);
                                    return;
                                  }
                                  toggleInnerExpand(i, 0);
                                }}
                                style={{ padding: '0' }}
                              >
                                <span style={{ fontSize: '14px', padding: '0' }}>
                                  {shortenandHoverString(parentCostingEntry?.productInfo?.reference || parentCostingEntry?.reference || '--', 24)}
                                </span>
                                {expandedRowIndex?.includes(i) && innerExpandedRowIndex?.[`${i}-0`] ? (
                                  <span className="icon-new-down-arrow-icon icon-inverse ml-2" />
                                ) : (
                                  <span className="icon-new-down-arrow-icon ml-2" />
                                )}
                              </button>
                              <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                                <button type="button" className="btn d-inline-flex p-0" aria-label="button" onClick={() => handleEdit(parentCostingEntry)}>
                                  <span className="icon-redirect f-15" />
                                </button>
                              </CustomTooltip>
                            </>
                          ) : (
                            <>
                              <span style={{ fontSize: '14px', padding: '0' }}>
                                {shortenandHoverString(parentCostingEntry?.productInfo?.reference || parentCostingEntry?.reference || '--', 24)}
                              </span>
                              <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                                <button
                                  type="button"
                                  className="btn d-inline-flex p-0"
                                  aria-label="button"
                                  onClick={() => handleEdit(parentCostingEntry, cost)}
                                >
                                  <span className="icon-redirect f-15" />
                                </button>
                              </CustomTooltip>
                            </>
                          )
                        ) : (
                          <>
                            <span style={{ fontSize: '14px', padding: '0' }}>
                              {shortenandHoverString(parentCostingEntry?.productInfo?.reference || parentCostingEntry?.reference || '--', 24)}
                            </span>
                            {parentCostingEntry?.productInfo?.reference && (
                              <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                                <button
                                  type="button"
                                  className="btn d-inline-flex p-0"
                                  aria-label="button"
                                  onClick={() => handleEdit(parentCostingEntry, cost)}
                                >
                                  <span className="icon-redirect f-15" />
                                </button>
                              </CustomTooltip>
                            )}
                          </>
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('supplier') && (
                      <td>
                        {shortenandHoverString(
                          parentCostingEntry?.supplierName?.supplierName || parentCostingEntry?.productInfo?.supplierId?.supplierName || '--',
                          24,
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('factory') && <td>{shortenandHoverString(parentCostingEntry?.productInfo?.factoryId?.name || '--', 24)}</td>}
                    {selectedColumns?.includes('currency') && <td>{shortenandHoverString(parentCostingEntry?.productInfo?.currency || '--', 20)}</td>}
                    {selectedColumns?.includes('moqNeeded') && (
                      <td>{shortenandHoverString(parentCostingEntry?.productInfo?.moqNeeded?.toString() || '--', 24)}</td>
                    )}
                    {selectedColumns?.includes('targetCost') && (
                      <td>
                        {shortenandHoverString(
                          formatCurrencyValue(
                            setCurrency(parentCostingEntry?.landedExchangeRateInformation?.companyCurrency),
                            parentCostingEntry?.productInfo?.targetCost || '0',
                          ),
                          24,
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('incoterm') && <td>{shortenandHoverString(parentCostingEntry?.productInfo?.incoterms?.name || '--', 32)}</td>}
                    {selectedColumns?.includes('shipmentMethod') && (
                      <td>{shortenandHoverString(parentCostingEntry?.productInfo?.shipmentMethod || '--', 24)}</td>
                    )}
                    {selectedColumns?.includes('sampleRef') && <td>{shortenandHoverString(getSampleRefLabel(parentCostingEntry?.sampleRef) || '--', 24)}</td>}
                    {isCostingInfoActive &&
                      dynamicCostingInfo?.map((dynCol, indxDyn) => (
                        <td className="w-150px" key={`dyn-parent-${dynCol?._id || indxDyn}`}>
                          {shortenandHoverString(getDynamicCostingValue(parentCostingEntry, dynCol), 18)}
                        </td>
                      ))}
                    <td>{shortenandHoverString(parentCostingEntry?.isAverageCosting ? 'Average' : 'Detailed', 20)}</td>
                    {selectedColumns?.includes('colourway') && (
                      <td>
                        {shortenandHoverString(
                          hasExpanded
                            ? rowOneData?.colourway?.name || '--'
                            : cost?.initialData?.colourways?.length === 1
                            ? cost?.initialData?.colourways[0]?.name
                            : '--',
                          15,
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('size') && (
                      <td>
                        {shortenandHoverString(
                          hasExpanded ? rowOneData?.size || '--' : cost?.initialData?.sizes?.length === 1 ? cost?.initialData?.sizes[0] : '--',
                          20,
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('length') && (
                      <td>
                        {shortenandHoverString(
                          hasExpanded
                            ? rowOneData?.length?.name || '--'
                            : cost?.initialData?.lengths?.length === 1
                            ? cost?.initialData?.lengths[0]?.name
                            : '--',
                          20,
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('quantity') && (
                      <td>{shortenandHoverString(rowOneData?.landedData?.quantityPerSizCol?.toString() || '0', 20)}</td>
                    )}
                    {selectedColumns?.includes('pricePerUnitIncoterm') && (
                      <td>
                        {shortenandHoverString(
                          formatCurrencyValue(setCurrency(cost?.initialData?.productInfo?.currency), rowOneData?.landedData?.pricePerUnitInco || '0'),
                          24,
                        )}
                      </td>
                    )}
                    {selectedColumns?.includes('totalIncoterm') && (
                      <td>
                        {shortenandHoverString(
                          formatCurrencyValue(setCurrency(parentCostingEntry?.productInfo?.currency), rowOneData?.landedData?.totalInco || '0'),
                          10,
                        )}
                      </td>
                    )}
                    {!isSupplierLoggedIn && (
                      <>
                        {isArray(topColumnsArr) &&
                          topColumnsArr?.map((topCol, indxTop) => {
                            const currency = setCurrency(cost?.initialData?.landedExchangeRateInformation?.companyCurrency);
                            const value = roundOff(rowOneData?.landedData?.[topCol?.aliasName], 2) || '0';
                            const displayValue = formatCurrencyValue(currency, value);
                            return (
                              <td key={indxTop} className="w-150px">
                                {shortenandHoverString(displayValue, 18)}
                              </td>
                            );
                          })}
                        {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                          activeCostingTemplateDetailes?.landedInfo
                            ?.filter((vl) => vl?.isActive)
                            ?.map((landedCol, indxLanded) => {
                              const currency = setCurrency(cost?.initialData?.landedExchangeRateInformation?.companyCurrency);
                              const value = roundOff(getLandedValue(rowOneData?.landedData?.landedInfo, landedCol?._id, landedCol?.type), 2, true) || '0';
                              const displayValue = formatCurrencyValue(currency, value);
                              return (
                                <td key={indxLanded} className="w-150px">
                                  {shortenandHoverString(displayValue, 18)}
                                </td>
                              );
                            })}
                        {Array.isArray(defaultColumnsArr) &&
                          !isEmpty(defaultColumnsArr) &&
                          defaultColumnsArr?.map((colDefault, indexDefault) => {
                            const currency = setCurrency(cost?.initialData?.landedExchangeRateInformation?.companyCurrency);
                            const value =
                              roundOff(
                                rowOneData?.landedData?.[`${colDefault?.aliasName}CalculatedValue`] || rowOneData?.landedData?.[colDefault?.aliasName] || '0',
                                2,
                                true,
                              ) || '0';
                            const displayValue = formatCurrencyValue(currency, value);
                            return colDefault?.isActive ? (
                              <td key={indexDefault} className="w-150px">
                                {shortenandHoverString(displayValue, 18)}
                              </td>
                            ) : null;
                          })}
                        {isArray(bottomColumnsArr) &&
                          bottomColumnsArr?.map((bottomCol, indxBottom) => {
                            let displayValue = '0';
                            if (['marginPerUnit']?.includes(bottomCol?.aliasName)) {
                              const value = roundOff(rowOneData?.landedData?.[bottomCol?.aliasName], 2, true) || '0';
                              displayValue = formatPercentageValue(value);
                            } else {
                              const costingCurrency = setCurrency(cost?.initialData?.landedExchangeRateInformation?.companyCurrency);
                              const rawOrCalculated = ['vat', 'customerDiscount']?.includes(bottomCol?.aliasName)
                                ? rowOneData?.landedData?.[`${bottomCol?.aliasName}CalculatedValue`] || rowOneData?.landedData?.[bottomCol?.aliasName]
                                : rowOneData?.landedData?.[bottomCol?.aliasName];
                              const value = roundOff(rawOrCalculated, 2, true) || '0';
                              displayValue = formatCurrencyValue(costingCurrency, value);
                            }
                            return bottomCol?.isActive ? (
                              <td key={indxBottom} className="w-150px">
                                {shortenandHoverString(displayValue, 18)}
                              </td>
                            ) : null;
                          })}
                      </>
                    )}
                    <td className="w-150px last-fix-col">
                      {expandedList.length === 1 && (
                        <>
                          {shortenandHoverString(getValue(expandedList[0]?.status), 16)}
                          <span className={`col-status right ${(expandedList[0]?.status || '').toLowerCase().replace(/\s+/g, '-')}`} />
                        </>
                      )}
                    </td>
                  </tr>
                  {((expandedRowIndex?.includes(i) && expandedList.length > 1) ||
                    (isRangePlanCosting && expandedList.length === 1 && firstCostingHasExpanded && innerExpandedRowIndex?.[`${i}-0`])) &&
                    isRangePlanCosting &&
                    expandedList.length > 1 &&
                    expandedRowIndex?.includes(i) &&
                    innerExpandedRowIndex?.[`${i}-0`] &&
                    Array.isArray(defaultExpandedList) &&
                    defaultExpandedList.length > 1 &&
                    defaultExpandedList.slice(1).map((combo, comboIndex) => {
                      const landedInforData = cloneDeep(combo?.landedData?.landedInfo);
                      return (
                        <tr className="expand-row" key={`${i}-default-combo-${comboIndex}`}>
                          {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && <td />}
                          <td />
                          <td />
                          <td />
                          {selectedColumns?.includes('reference') && <td className="first-fix-col" />}
                          {selectedColumns?.includes('supplier') && <td />}
                          {selectedColumns?.includes('factory') && <td />}
                          {selectedColumns?.includes('currency') && <td>{shortenandHoverString(defaultCostingEntry?.productInfo?.currency || '--', 20)}</td>}
                          {selectedColumns?.includes('moqNeeded') && <td />}
                          {selectedColumns?.includes('targetCost') && (
                            <td>
                              {shortenandHoverString(
                                formatCurrencyValue(
                                  setCurrency(defaultCostingEntry?.landedExchangeRateInformation?.companyCurrency),
                                  defaultCostingEntry?.productInfo?.targetCost || '0',
                                ),
                                24,
                              )}
                            </td>
                          )}
                          {selectedColumns?.includes('incoterm') && (
                            <td>{shortenandHoverString(defaultCostingEntry?.productInfo?.incoterms?.name || '--', 32)}</td>
                          )}
                          {selectedColumns?.includes('shipmentMethod') && (
                            <td>{shortenandHoverString(defaultCostingEntry?.productInfo?.shipmentMethod || '--', 24)}</td>
                          )}
                          {selectedColumns?.includes('sampleRef') && <td />}
                          {isCostingInfoActive &&
                            dynamicCostingInfo?.map((dynCol, indxDyn) => <td className="w-150px" key={`dyn-child-${dynCol?._id || indxDyn}`} />)}
                          <td />
                          {selectedColumns?.includes('colourway') && <td>{shortenandHoverString(combo?.colourway?.name || '--', 15)}</td>}
                          {selectedColumns?.includes('size') && <td>{shortenandHoverString(combo?.size || '--', 20)}</td>}
                          {selectedColumns?.includes('length') && <td>{shortenandHoverString(combo?.length?.name || '--', 20)}</td>}
                          {selectedColumns?.includes('quantity') && (
                            <td>{shortenandHoverString(combo?.landedData?.quantityPerSizCol?.toString() || '--', 20)}</td>
                          )}
                          {selectedColumns?.includes('pricePerUnitIncoterm') && (
                            <td>
                              {shortenandHoverString(
                                formatCurrencyValue(
                                  setCurrency(defaultCostingEntry?.productInfo?.currency),
                                  roundOff(combo?.landedData?.pricePerUnitInco, 2, true) || '0',
                                ),
                                24,
                              )}
                            </td>
                          )}
                          {selectedColumns?.includes('totalIncoterm') && (
                            <td>
                              {shortenandHoverString(
                                formatCurrencyValue(
                                  setCurrency(defaultCostingEntry?.productInfo?.currency),
                                  roundOff(combo?.landedData?.totalInco, 2, true) || '0',
                                ),
                                24,
                              )}
                            </td>
                          )}
                          {!isSupplierLoggedIn && (
                            <>
                              {isArray(topColumnsArr) &&
                                topColumnsArr?.map((topCol, indxTop) => {
                                  const currency = ['pricePerUnitIncoConverted', 'totalIncoConverted']?.includes(topCol?.aliasName)
                                    ? setCurrency(defaultCostingEntry?.landedExchangeRateInformation?.companyCurrency)
                                    : '';
                                  const value = roundOff(combo?.landedData?.[topCol?.aliasName], 2, true) || '0';
                                  const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                  return (
                                    <td key={indxTop} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  );
                                })}
                              {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                                activeCostingTemplateDetailes?.landedInfo?.map((landedCol, indxLanded) => {
                                  const currency = setCurrency(defaultCostingEntry?.landedExchangeRateInformation?.companyCurrency);
                                  const value = roundOff(getLandedValue(landedInforData, landedCol?._id, landedCol?.type), 2, true) || '0';
                                  const displayValue = formatCurrencyValue(currency, value);
                                  return landedCol?.isActive ? (
                                    <td key={indxLanded} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  ) : null;
                                })}
                              {Array.isArray(defaultColumnsArr) &&
                                !isEmpty(defaultColumnsArr) &&
                                defaultColumnsArr?.map((colDefault, indexDefault) => {
                                  const currency = [
                                    'freightValue',
                                    'freightAutomated',
                                    'agentsFee',
                                    'freightPercentage',
                                    'licenseRoyaltyFee',
                                    'likenessRoyaltyFee',
                                    'dutyPercentage',
                                    'tariffCostPercentage',
                                  ]?.includes(colDefault?.aliasName)
                                    ? setCurrency(defaultCostingEntry?.landedExchangeRateInformation?.companyCurrency)
                                    : '';
                                  const value =
                                    roundOff(
                                      combo?.landedData?.[`${colDefault?.aliasName}CalculatedValue`] || combo?.landedData?.[colDefault?.aliasName] || '0',
                                      2,
                                      true,
                                    ) || '0';
                                  const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                  return colDefault?.isActive ? (
                                    <td key={indexDefault} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  ) : null;
                                })}
                              {isArray(bottomColumnsArr) &&
                                bottomColumnsArr?.map((bottomCol, indxBottom) => {
                                  const currency = [
                                    'pricePerUnitLanded',
                                    'totalLanded',
                                    'sellingPriceGross',
                                    'sellingPriceNet',
                                    'grossProfitPerUnit',
                                    'markupPerUnit',
                                    'estimatedRetailValue',
                                    'estimatedProfitPerUnit',
                                    'marginPerUnit',
                                    'retailMarginPerUnit',
                                    'vat',
                                    'customerDiscount',
                                  ]?.includes(bottomCol?.aliasName)
                                    ? setCurrency(defaultCostingEntry?.landedExchangeRateInformation?.companyCurrency)
                                    : '';
                                  const rawOrCalculated = ['vat', 'customerDiscount']?.includes(bottomCol?.aliasName)
                                    ? combo?.landedData?.[`${bottomCol?.aliasName}CalculatedValue`] || combo?.landedData?.[bottomCol?.aliasName]
                                    : combo?.landedData?.[bottomCol?.aliasName];
                                  const value = roundOff(rawOrCalculated, 2, true) || '0';
                                  const suffix = ['marginPerUnit']?.includes(bottomCol?.aliasName) ? '%' : '';
                                  const displayValue = suffix ? formatPercentageValue(value) : currency ? formatCurrencyValue(currency, value) : value;
                                  return bottomCol?.isActive ? (
                                    <td key={indxBottom} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  ) : null;
                                })}
                            </>
                          )}
                          <td className="last-fix-col" />
                        </tr>
                      );
                    })}

                  {((expandedRowIndex?.includes(i) && expandedList.length > 1) ||
                    (isRangePlanCosting && expandedList.length === 1 && firstCostingHasExpanded && innerExpandedRowIndex?.[`${i}-0`])) &&
                    childCostingList?.map((expData, indParent) => {
                      if (isRangePlanCosting) {
                        const costingEntry = expData;
                        const originalIndex = expandedList.indexOf(costingEntry);
                        const costingKey = `${i}-${originalIndex}`;
                        const isCostingExpanded = !!innerExpandedRowIndex[costingKey];
                        const rawCostingExpandedList =
                          Array.isArray(costingEntry?.expandedData) && costingEntry.expandedData.length > 0 ? costingEntry.expandedData : [];
                        const costingExpandedList = rawCostingExpandedList.reduce(
                          (acc, combo) => {
                            const colourwayId = combo?.colourway?._id || combo?.colourway?.id || combo?.colourway || '';
                            const sizeId = combo?.size || '';
                            const lengthId = combo?.length?._id || combo?.length?.id || combo?.length?.name || combo?.length || '';
                            const key = `${colourwayId}__${sizeId}__${lengthId}`;
                            if (!acc._seen.has(key)) {
                              acc._seen.add(key);
                              acc.list.push(combo);
                            }
                            return acc;
                          },
                          { _seen: new Set(), list: [] },
                        ).list;
                        const costingHasExpanded = costingExpandedList.length > 1;
                        const costingRowOneData = costingExpandedList?.[1] || costingExpandedList?.[0] || {};
                        const rows = [];

                        if (expandedList.length > 1) {
                          rows.push(
                            <tr className="expand-row" key={`${costingKey}-main`}>
                              {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && <td />}
                              <td />
                              <td />
                              <td />
                              {selectedColumns?.includes('reference') && (
                                <td className="first-fix-col" style={!costingHasExpanded ? { paddingLeft: '8px' } : {}}>
                                  {costingHasExpanded ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn p-0 d-inline-flex align-items-center"
                                        aria-label={t('COMMON_LABEL.COLLAPSE')}
                                        onClick={() => toggleInnerExpand(i, originalIndex)}
                                        style={{ marginLeft: '15px', padding: '0' }}
                                      >
                                        <span style={{ fontSize: '14px', padding: '0', marginLeft: '-16px' }}>
                                          {shortenandHoverString(costingEntry?.productInfo?.reference || costingEntry?.reference || '--', 24)}
                                        </span>
                                        {isCostingExpanded ? (
                                          <span className="icon-new-down-arrow-icon icon-inverse ml-2" />
                                        ) : (
                                          <span className="icon-new-down-arrow-icon ml-2" />
                                        )}
                                      </button>
                                      <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                                        <button type="button" className="btn d-inline-flex p-0" aria-label="button" onClick={() => handleEdit(costingEntry)}>
                                          <span className="icon-redirect f-15" />
                                        </button>
                                      </CustomTooltip>
                                    </>
                                  ) : (
                                    <>
                                      <span style={{ fontSize: '14px' }}>
                                        {shortenandHoverString(costingEntry?.productInfo?.reference || costingEntry?.reference || '--', 24)}
                                      </span>
                                      <CustomTooltip tooltipText={t('ADD_PO_TEMPLATE_PROVIDER.EDITS')}>
                                        <button type="button" className="btn d-inline-flex p-0" aria-label="button" onClick={() => handleEdit(costingEntry)}>
                                          <span className="icon-redirect f-15" />
                                        </button>
                                      </CustomTooltip>
                                    </>
                                  )}
                                </td>
                              )}
                              {selectedColumns?.includes('supplier') && (
                                <td>{shortenandHoverString(costingEntry?.productInfo?.supplierId?.supplierName || '--', 24)}</td>
                              )}
                              {selectedColumns?.includes('factory') && <td>{shortenandHoverString(costingEntry?.productInfo?.factoryId?.name || '--', 24)}</td>}
                              {selectedColumns?.includes('currency') && <td>{shortenandHoverString(costingEntry?.productInfo?.currency || '--', 20)}</td>}
                              {selectedColumns?.includes('moqNeeded') && (
                                <td>{shortenandHoverString(costingEntry?.productInfo?.moqNeeded?.toString() || '--', 24)}</td>
                              )}
                              {selectedColumns?.includes('targetCost') && (
                                <td>
                                  {shortenandHoverString(
                                    formatCurrencyValue(
                                      setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency),
                                      costingEntry?.productInfo?.targetCost || costingEntry?.targetCost || '0',
                                    ),
                                    24,
                                  )}
                                </td>
                              )}
                              {selectedColumns?.includes('incoterm') && (
                                <td>{shortenandHoverString(costingEntry?.productInfo?.incoterms?.name || '--', 32)}</td>
                              )}
                              {selectedColumns?.includes('shipmentMethod') && (
                                <td>{shortenandHoverString(costingEntry?.productInfo?.shipmentMethod || '--', 24)}</td>
                              )}
                              {selectedColumns?.includes('sampleRef') && <td>{shortenandHoverString(getSampleRefLabel(costingEntry?.sampleRef) || '--', 24)}</td>}
                              {isCostingInfoActive &&
                                dynamicCostingInfo?.map((dynCol, indxDyn) => <td className="w-150px" key={`dyn-child-${dynCol?._id || indxDyn}`} />)}
                              <td>{shortenandHoverString(costingEntry?.isAverageCosting ? 'Average' : 'Detailed', 20)}</td>
                              {selectedColumns?.includes('colourway') && (
                                <td>
                                  {shortenandHoverString(
                                    costingHasExpanded
                                      ? costingRowOneData?.colourway?.name || '--'
                                      : costingEntry?.colourways?.length === 1
                                      ? costingEntry?.colourways[0]?.name
                                      : costingRowOneData?.colourway?.name || '--',
                                    15,
                                  )}
                                </td>
                              )}
                              {selectedColumns?.includes('size') && (
                                <td>
                                  {shortenandHoverString(
                                    costingHasExpanded
                                      ? costingRowOneData?.size || '--'
                                      : costingEntry?.sizes?.length === 1
                                      ? costingEntry?.sizes[0]
                                      : costingRowOneData?.size || '--',
                                    20,
                                  )}
                                </td>
                              )}
                              {selectedColumns?.includes('length') && (
                                <td>
                                  {shortenandHoverString(
                                    costingHasExpanded
                                      ? costingRowOneData?.length?.name || '--'
                                      : costingEntry?.lengths?.length === 1
                                      ? costingEntry?.lengths[0]?.name
                                      : costingRowOneData?.length?.name || '--',
                                    20,
                                  )}
                                </td>
                              )}
                              {selectedColumns?.includes('quantity') && (
                                <td>{shortenandHoverString(costingRowOneData?.landedData?.quantityPerSizCol?.toString() || '0', 20)}</td>
                              )}
                              {selectedColumns?.includes('pricePerUnitIncoterm') && (
                                <td>
                                  {shortenandHoverString(
                                    formatCurrencyValue(
                                      setCurrency(costingEntry?.productInfo?.currency),
                                      roundOff(costingRowOneData?.landedData?.pricePerUnitInco, 2, true) || '0',
                                    ),
                                    24,
                                  )}
                                </td>
                              )}
                              {selectedColumns?.includes('totalIncoterm') && (
                                <td>
                                  {shortenandHoverString(
                                    formatCurrencyValue(
                                      setCurrency(costingEntry?.productInfo?.currency),
                                      roundOff(costingRowOneData?.landedData?.totalInco, 2, true) || '0',
                                    ),
                                    10,
                                  )}
                                </td>
                              )}
                              {!isSupplierLoggedIn && (
                                <>
                                  {isArray(topColumnsArr) &&
                                    topColumnsArr?.map((topCol, indxTop) => {
                                      const currency = setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency);
                                      const value = roundOff(costingRowOneData?.landedData?.[topCol?.aliasName], 2) || '0';
                                      const displayValue = formatCurrencyValue(currency, value);
                                      return (
                                        <td key={indxTop} className="w-150px">
                                          {shortenandHoverString(displayValue, 18)}
                                        </td>
                                      );
                                    })}
                                  {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                                    activeCostingTemplateDetailes?.landedInfo
                                      ?.filter((vl) => vl?.isActive)
                                      ?.map((landedCol, indxLanded) => {
                                        const currency = setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency);
                                        const value =
                                          roundOff(getLandedValue(costingRowOneData?.landedData?.landedInfo, landedCol?._id, landedCol?.type), 2, true) || '0';
                                        const displayValue = formatCurrencyValue(currency, value);
                                        return (
                                          <td key={indxLanded} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        );
                                      })}
                                  {Array.isArray(defaultColumnsArr) &&
                                    !isEmpty(defaultColumnsArr) &&
                                    defaultColumnsArr?.map((colDefault, indexDefault) => {
                                      const currency = setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency);
                                      const value =
                                        roundOff(
                                          costingRowOneData?.landedData?.[`${colDefault?.aliasName}CalculatedValue`] ||
                                            costingRowOneData?.landedData?.[colDefault?.aliasName] ||
                                            '0',
                                          2,
                                          true,
                                        ) || '0';
                                      const displayValue = formatCurrencyValue(currency, value);
                                      return colDefault?.isActive ? (
                                        <td key={indexDefault} className="w-150px">
                                          {shortenandHoverString(displayValue, 18)}
                                        </td>
                                      ) : null;
                                    })}
                                  {isArray(bottomColumnsArr) &&
                                    bottomColumnsArr?.map((bottomCol, indxBottom) => {
                                      let displayValue = '0';
                                      if (['marginPerUnit']?.includes(bottomCol?.aliasName)) {
                                        const value = roundOff(costingRowOneData?.landedData?.[bottomCol?.aliasName], 2, true) || '0';
                                        displayValue = formatPercentageValue(value);
                                      } else {
                                        const currency = setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency);
                                        const rawOrCalculated = ['vat', 'customerDiscount']?.includes(bottomCol?.aliasName)
                                          ? costingRowOneData?.landedData?.[`${bottomCol?.aliasName}CalculatedValue`] ||
                                            costingRowOneData?.landedData?.[bottomCol?.aliasName]
                                          : costingRowOneData?.landedData?.[bottomCol?.aliasName];
                                        const value = roundOff(rawOrCalculated, 2, true) || '0';
                                        displayValue = formatCurrencyValue(currency, value);
                                      }
                                      return bottomCol?.isActive ? (
                                        <td key={indxBottom} className="w-150px">
                                          {shortenandHoverString(displayValue, 18)}
                                        </td>
                                      ) : null;
                                    })}
                                </>
                              )}
                              <td className="w-150px last-fix-col">
                                {shortenandHoverString(getValue(cost?.initialData?.status), 16)}
                                <span className={`col-status right ${`${cost?.initialData?.status || ''}`.toLowerCase().replace(/\s+/g, '-')}`} />
                              </td>{' '}
                            </tr>,
                          );
                        }

                        if (expandedList.length > 1 && isCostingExpanded && costingExpandedList.length > 1) {
                          costingExpandedList.slice(1).forEach((combo, comboIndex) => {
                            const landedInforData = cloneDeep(combo?.landedData?.landedInfo);
                            rows.push(
                              <tr className="expand-row" key={`${costingKey}-combo-${comboIndex}`}>
                                {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && <td />}
                                <td />
                                <td />
                                <td />
                                {selectedColumns?.includes('reference') && <td className="first-fix-col" />}
                                {selectedColumns?.includes('supplier') && <td />}
                                {selectedColumns?.includes('factory') && <td />}
                                {selectedColumns?.includes('currency') && <td>{shortenandHoverString(costingEntry?.productInfo?.currency || '--', 20)}</td>}
                                {selectedColumns?.includes('moqNeeded') && <td />}
                                {selectedColumns?.includes('targetCost') && (
                                  <td>
                                    {shortenandHoverString(
                                      formatCurrencyValue(
                                        setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency),
                                        costingEntry?.productInfo?.targetCost || '0',
                                      ),
                                      24,
                                    )}
                                  </td>
                                )}
                                {selectedColumns?.includes('incoterm') && (
                                  <td>{shortenandHoverString(costingEntry?.productInfo?.incoterms?.name || '--', 32)}</td>
                                )}
                                {selectedColumns?.includes('shipmentMethod') && (
                                  <td>{shortenandHoverString(costingEntry?.productInfo?.shipmentMethod || '--', 24)}</td>
                                )}
                                {selectedColumns?.includes('sampleRef') && <td />}
                                {isCostingInfoActive &&
                                  dynamicCostingInfo?.map((dynCol, indxDyn) => <td className="w-150px" key={`dyn-child-${dynCol?._id || indxDyn}`} />)}
                                <td />
                                {selectedColumns?.includes('colourway') && <td>{shortenandHoverString(combo?.colourway?.name || '--', 15)}</td>}
                                {selectedColumns?.includes('size') && <td>{shortenandHoverString(combo?.size || '--', 20)}</td>}
                                {selectedColumns?.includes('length') && <td>{shortenandHoverString(combo?.length?.name || '--', 20)}</td>}
                                {selectedColumns?.includes('quantity') && (
                                  <td>{shortenandHoverString(combo?.landedData?.quantityPerSizCol?.toString() || '--', 20)}</td>
                                )}
                                {selectedColumns?.includes('pricePerUnitIncoterm') && (
                                  <td>
                                    {shortenandHoverString(
                                      formatCurrencyValue(
                                        setCurrency(costingEntry?.productInfo?.currency),
                                        roundOff(combo?.landedData?.pricePerUnitInco, 2, true) || '0',
                                      ),
                                      24,
                                    )}
                                  </td>
                                )}
                                {selectedColumns?.includes('totalIncoterm') && (
                                  <td>
                                    {shortenandHoverString(
                                      formatCurrencyValue(
                                        setCurrency(costingEntry?.productInfo?.currency),
                                        roundOff(combo?.landedData?.totalInco, 2, true) || '0',
                                      ),
                                      24,
                                    )}
                                  </td>
                                )}
                                {!isSupplierLoggedIn && (
                                  <>
                                    {isArray(topColumnsArr) &&
                                      topColumnsArr?.map((topCol, indxTop) => {
                                        const currency = ['pricePerUnitIncoConverted', 'totalIncoConverted']?.includes(topCol?.aliasName)
                                          ? setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency)
                                          : '';
                                        const value = roundOff(combo?.landedData?.[topCol?.aliasName], 2, true) || '0';
                                        const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                        return (
                                          <td key={indxTop} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        );
                                      })}
                                    {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                                      activeCostingTemplateDetailes?.landedInfo?.map((landedCol, indxLanded) => {
                                        const currency = setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency);
                                        const value = roundOff(getLandedValue(landedInforData, landedCol?._id, landedCol?.type), 2, true) || '0';
                                        const displayValue = formatCurrencyValue(currency, value);
                                        return landedCol?.isActive ? (
                                          <td key={indxLanded} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        ) : null;
                                      })}
                                    {Array.isArray(defaultColumnsArr) &&
                                      !isEmpty(defaultColumnsArr) &&
                                      defaultColumnsArr?.map((colDefault, indexDefault) => {
                                        const currency = [
                                          'freightValue',
                                          'freightAutomated',
                                          'agentsFee',
                                          'freightPercentage',
                                          'licenseRoyaltyFee',
                                          'likenessRoyaltyFee',
                                          'dutyPercentage',
                                          'tariffCostPercentage',
                                        ]?.includes(colDefault?.aliasName)
                                          ? setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency)
                                          : '';
                                        const value =
                                          roundOff(
                                            combo?.landedData?.[`${colDefault?.aliasName}CalculatedValue`] || combo?.landedData?.[colDefault?.aliasName] || '0',
                                            2,
                                            true,
                                          ) || '0';
                                        const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                        return colDefault?.isActive ? (
                                          <td key={indexDefault} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        ) : null;
                                      })}
                                    {isArray(bottomColumnsArr) &&
                                      bottomColumnsArr?.map((bottomCol, indxBottom) => {
                                        const currency = [
                                          'pricePerUnitLanded',
                                          'totalLanded',
                                          'sellingPriceGross',
                                          'sellingPriceNet',
                                          'grossProfitPerUnit',
                                          'markupPerUnit',
                                          'estimatedRetailValue',
                                          'estimatedProfitPerUnit',
                                          'marginPerUnit',
                                          'retailMarginPerUnit',
                                          'vat',
                                          'customerDiscount',
                                        ]?.includes(bottomCol?.aliasName)
                                          ? setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency)
                                          : '';
                                        const rawOrCalculated = ['vat', 'customerDiscount']?.includes(bottomCol?.aliasName)
                                          ? combo?.landedData?.[`${bottomCol?.aliasName}CalculatedValue`] || combo?.landedData?.[bottomCol?.aliasName]
                                          : combo?.landedData?.[bottomCol?.aliasName];
                                        const value = roundOff(rawOrCalculated, 2, true) || '0';
                                        const suffix = ['marginPerUnit']?.includes(bottomCol?.aliasName) ? '%' : '';
                                        const displayValue = suffix ? formatPercentageValue(value) : currency ? formatCurrencyValue(currency, value) : value;
                                        return bottomCol?.isActive ? (
                                          <td key={indxBottom} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        ) : null;
                                      })}
                                  </>
                                )}
                                <td className="last-fix-col" />
                              </tr>,
                            );
                          });
                        }
                        if (expandedList.length === 1) {
                          costingExpandedList.slice(1).forEach((combo, comboIndex) => {
                            rows.push(
                              <tr className="expand-row" key={`${costingKey}-combo-${comboIndex}`}>
                                {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && <td />}
                                <td />
                                <td />
                                <td />
                                {selectedColumns?.includes('reference') && <td className="first-fix-col" />}
                                {selectedColumns?.includes('supplier') && <td />}
                                {selectedColumns?.includes('factory') && <td />}
                                {selectedColumns?.includes('currency') && <td>{shortenandHoverString(costingEntry?.productInfo?.currency || '--', 20)}</td>}
                                {selectedColumns?.includes('moqNeeded') && <td />}
                                {selectedColumns?.includes('targetCost') && (
                                  <td>
                                    {shortenandHoverString(
                                      formatCurrencyValue(
                                        setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency),
                                        costingEntry?.productInfo?.targetCost || '0',
                                      ),
                                      24,
                                    )}
                                  </td>
                                )}
                                {selectedColumns?.includes('incoterm') && (
                                  <td>{shortenandHoverString(costingEntry?.productInfo?.incoterms?.name || '--', 32)}</td>
                                )}
                                {selectedColumns?.includes('shipmentMethod') && (
                                  <td>{shortenandHoverString(costingEntry?.productInfo?.shipmentMethod || '--', 24)}</td>
                                )}
                                {selectedColumns?.includes('sampleRef') && <td />}
                                {isCostingInfoActive &&
                                  dynamicCostingInfo?.map((dynCol, indxDyn) => <td className="w-150px" key={`dyn-child-${dynCol?._id || indxDyn}`} />)}
                                <td />
                                {selectedColumns?.includes('colourway') && <td>{shortenandHoverString(combo?.colourway?.name || '--', 15)}</td>}
                                {selectedColumns?.includes('size') && <td>{shortenandHoverString(combo?.size || '--', 20)}</td>}
                                {selectedColumns?.includes('length') && <td>{shortenandHoverString(combo?.length?.name || '--', 20)}</td>}
                                {selectedColumns?.includes('quantity') && (
                                  <td>{shortenandHoverString(combo?.landedData?.quantityPerSizCol?.toString() || '0', 20)}</td>
                                )}
                                {selectedColumns?.includes('pricePerUnitIncoterm') && (
                                  <td>
                                    {shortenandHoverString(
                                      formatCurrencyValue(
                                        setCurrency(costingEntry?.productInfo?.currency),
                                        roundOff(combo?.landedData?.pricePerUnitInco, 2, true) || '0',
                                      ),
                                      24,
                                    )}
                                  </td>
                                )}
                                {selectedColumns?.includes('totalIncoterm') && (
                                  <td>
                                    {shortenandHoverString(
                                      formatCurrencyValue(
                                        setCurrency(costingEntry?.productInfo?.currency),
                                        roundOff(combo?.landedData?.totalInco, 2, true) || '0',
                                      ),
                                      24,
                                    )}
                                  </td>
                                )}
                                {!isSupplierLoggedIn && (
                                  <>
                                    {isArray(topColumnsArr) &&
                                      topColumnsArr?.map((topCol, indxTop) => {
                                        const currency = ['pricePerUnitIncoConverted', 'totalIncoConverted']?.includes(topCol?.aliasName)
                                          ? setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency)
                                          : '';
                                        const value = roundOff(combo?.landedData?.[topCol?.aliasName], 2, true) || '0';
                                        const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                        return (
                                          <td key={indxTop} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        );
                                      })}
                                    {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                                      activeCostingTemplateDetailes?.landedInfo
                                        ?.filter((vl) => vl?.isActive)
                                        ?.map((landedCol, indxLanded) => {
                                          const currency = setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency);
                                          const value =
                                            roundOff(getLandedValue(combo?.landedData?.landedInfo, landedCol?._id, landedCol?.type), 2, true) || '0';
                                          const displayValue = formatCurrencyValue(currency, value);
                                          return (
                                            <td key={indxLanded} className="w-150px">
                                              {shortenandHoverString(displayValue, 18)}
                                            </td>
                                          );
                                        })}
                                    {Array.isArray(defaultColumnsArr) &&
                                      !isEmpty(defaultColumnsArr) &&
                                      defaultColumnsArr?.map((colDefault, indexDefault) => {
                                        const currency = [
                                          'freightValue',
                                          'freightAutomated',
                                          'agentsFee',
                                          'freightPercentage',
                                          'licenseRoyaltyFee',
                                          'likenessRoyaltyFee',
                                          'dutyPercentage',
                                          'tariffCostPercentage',
                                        ]?.includes(colDefault?.aliasName)
                                          ? setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency)
                                          : '';
                                        const value =
                                          roundOff(
                                            combo?.landedData?.[`${colDefault?.aliasName}CalculatedValue`] || combo?.landedData?.[colDefault?.aliasName] || '0',
                                            2,
                                            true,
                                          ) || '0';
                                        const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                        return colDefault?.isActive ? (
                                          <td key={indexDefault} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        ) : null;
                                      })}
                                    {isArray(bottomColumnsArr) &&
                                      bottomColumnsArr?.map((bottomCol, indxBottom) => {
                                        const currency = [
                                          'pricePerUnitLanded',
                                          'totalLanded',
                                          'sellingPriceGross',
                                          'sellingPriceNet',
                                          'grossProfitPerUnit',
                                          'markupPerUnit',
                                          'estimatedRetailValue',
                                          'estimatedProfitPerUnit',
                                          'marginPerUnit',
                                          'retailMarginPerUnit',
                                          'vat',
                                          'customerDiscount',
                                        ]?.includes(bottomCol?.aliasName)
                                          ? setCurrency(costingEntry?.landedExchangeRateInformation?.companyCurrency)
                                          : '';
                                        const rawOrCalculated = ['vat', 'customerDiscount']?.includes(bottomCol?.aliasName)
                                          ? combo?.landedData?.[`${bottomCol?.aliasName}CalculatedValue`] || combo?.landedData?.[bottomCol?.aliasName]
                                          : combo?.landedData?.[bottomCol?.aliasName];
                                        const value = roundOff(rawOrCalculated, 2, true) || '0';
                                        const suffix = ['marginPerUnit']?.includes(bottomCol?.aliasName) ? '%' : '';
                                        const displayValue = suffix ? formatPercentageValue(value) : currency ? formatCurrencyValue(currency, value) : value;
                                        return bottomCol?.isActive ? (
                                          <td key={indxBottom} className="w-150px">
                                            {shortenandHoverString(displayValue, 18)}
                                          </td>
                                        ) : null;
                                      })}
                                  </>
                                )}
                                <td className="last-fix-col" />
                              </tr>,
                            );
                          });
                        }

                        return rows;
                      }

                      const landedInforData = cloneDeep(expData?.landedData?.landedInfo);
                      return (
                        <tr className="expand-row" key={indParent}>
                          {!isSupplier && (MasterAddEditAccess || MasterDeleteAccess) && <td />}
                          <td />
                          <td />
                          <td />
                          {selectedColumns?.includes('reference') && <td className="first-fix-col" />}
                          {selectedColumns?.includes('supplier') && <td />}
                          {selectedColumns?.includes('factory') && <td />}
                          {selectedColumns?.includes('currency') && <td>{shortenandHoverString(cost?.productInfo?.currency || '--', 20)}</td>}
                          {selectedColumns?.includes('moqNeeded') && <td />}
                          {selectedColumns?.includes('targetCost') && (
                            <td>
                              {shortenandHoverString(
                                formatCurrencyValue(setCurrency(cost?.landedExchangeRateInformation?.companyCurrency), cost?.productInfo?.targetCost || '0'),
                                24,
                              )}
                            </td>
                          )}
                          {selectedColumns?.includes('incoterm') && <td>{shortenandHoverString(cost?.productInfo?.incoterms?.name || '--', 32)}</td>}
                          {selectedColumns?.includes('shipmentMethod') && <td>{shortenandHoverString(cost?.productInfo?.shipmentMethod || '--', 24)}</td>}
                          {selectedColumns?.includes('sampleRef') && <td />}
                          {isCostingInfoActive &&
                            dynamicCostingInfo?.map((dynCol, indxDyn) => <td className="w-150px" key={`dyn-child-${dynCol?._id || indxDyn}`} />)}
                          <td />
                          {selectedColumns?.includes('colourway') && <td>{shortenandHoverString(expData?.colourway?.name || '--', 15)}</td>}
                          {selectedColumns?.includes('size') && <td>{shortenandHoverString(expData?.size || '--', 20)}</td>}
                          {selectedColumns?.includes('length') && <td>{shortenandHoverString(expData?.length?.name || '--', 20)}</td>}
                          {selectedColumns?.includes('quantity') && (
                            <td>{shortenandHoverString(expData?.landedData?.quantityPerSizCol?.toString() || '--', 20)}</td>
                          )}
                          {selectedColumns?.includes('pricePerUnitIncoterm') && (
                            <td>
                              {shortenandHoverString(
                                formatCurrencyValue(setCurrency(cost?.productInfo?.currency), roundOff(expData?.landedData?.pricePerUnitInco, 2, true) || '0'),
                                24,
                              )}
                            </td>
                          )}
                          {selectedColumns?.includes('totalIncoterm') && (
                            <td>
                              {shortenandHoverString(
                                formatCurrencyValue(setCurrency(cost?.productInfo?.currency), roundOff(expData?.landedData?.totalInco, 2, true) || '0'),
                                24,
                              )}
                            </td>
                          )}
                          {!isSupplierLoggedIn && (
                            <>
                              {isArray(topColumnsArr) &&
                                topColumnsArr?.map((topCol, indxTop) => {
                                  const currency = ['pricePerUnitIncoConverted', 'totalIncoConverted']?.includes(topCol?.aliasName)
                                    ? setCurrency(cost?.landedExchangeRateInformation?.companyCurrency)
                                    : '';
                                  const value = roundOff(expData?.landedData?.[topCol?.aliasName], 2, true) || '0';
                                  const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                  return (
                                    <td key={indxTop} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  );
                                })}
                              {isArray(activeCostingTemplateDetailes?.landedInfo) &&
                                activeCostingTemplateDetailes?.landedInfo?.map((landedCol, indxLanded) => {
                                  const currency = setCurrency(cost?.landedExchangeRateInformation?.companyCurrency);
                                  const value = roundOff(getLandedValue(landedInforData, landedCol?._id, landedCol?.type), 2, true) || '0';
                                  const displayValue = formatCurrencyValue(currency, value);
                                  return landedCol?.isActive ? (
                                    <td key={indxLanded} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  ) : null;
                                })}
                              {Array.isArray(defaultColumnsArr) &&
                                !isEmpty(defaultColumnsArr) &&
                                defaultColumnsArr?.map((colDefault, indexDefault) => {
                                  const currency = [
                                    'freightValue',
                                    'freightAutomated',
                                    'agentsFee',
                                    'freightPercentage',
                                    'licenseRoyaltyFee',
                                    'likenessRoyaltyFee',
                                    'dutyPercentage',
                                    'tariffCostPercentage',
                                  ]?.includes(colDefault?.aliasName)
                                    ? setCurrency(cost?.landedExchangeRateInformation?.companyCurrency)
                                    : '';
                                  const value =
                                    roundOff(
                                      expData?.landedData?.[`${colDefault?.aliasName}CalculatedValue`] || expData?.landedData?.[colDefault?.aliasName] || '0',
                                      2,
                                      true,
                                    ) || '0';
                                  const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                  return colDefault?.isActive ? (
                                    <td key={indexDefault} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  ) : null;
                                })}
                              {isArray(bottomColumnsArr) &&
                                bottomColumnsArr?.map((bottomCol, indxBottom) => {
                                  const currency = [
                                    'pricePerUnitLanded',
                                    'totalLanded',
                                    'sellingPriceGross',
                                    'sellingPriceNet',
                                    'grossProfitPerUnit',
                                    'markupPerUnit',
                                    'estimatedRetailValue',
                                    'estimatedProfitPerUnit',
                                    'marginPerUnit',
                                    'retailMarginPerUnit',
                                    'vat',
                                    'customerDiscount',
                                  ]?.includes(bottomCol?.aliasName)
                                    ? setCurrency(cost?.landedExchangeRateInformation?.companyCurrency)
                                    : '';
                                  const rawOrCalculated = ['vat', 'customerDiscount']?.includes(bottomCol?.aliasName)
                                    ? expData?.landedData?.[`${bottomCol?.aliasName}CalculatedValue`] || expData?.landedData?.[bottomCol?.aliasName]
                                    : expData?.landedData?.[bottomCol?.aliasName];
                                  const value = roundOff(rawOrCalculated, 2, true) || '0';
                                  const displayValue = currency ? formatCurrencyValue(currency, value) : value;
                                  return bottomCol?.isActive ? (
                                    <td key={indxBottom} className="w-150px">
                                      {shortenandHoverString(displayValue, 18)}
                                    </td>
                                  ) : null;
                                })}
                            </>
                          )}
                          <td className="last-fix-col" />
                        </tr>
                      );
                    })}
                </tbody>
              );
            })}
          </table>
        </InfiniteScroll>
      );
    }

    return initState?.costingData?.length === 0 && !isCostingDataLoading && <p className="text-center">{t('COSTING_TO_DO_LIST.NO_DATA_FOUND')}</p>;
  };

  return (
    <div id="rpcostingtablecontainer" className="rp-scroll-container">
      {renderContent()}
    </div>
  );
}

export default ProductCostingTable;
